import Vue from 'vue';
import Router from 'vue-router';
import Home from '@/views/Home.vue';
import cognitoAuth from '../cognito/cognito-service';
import Expired from '@/views/Expired.vue';
import ErrorComponent from '@/components/error/Error.vue';
import store from '../../store/store';
import ContentProfileRoutes from './content-profile-routes';
import { Auth } from 'aws-amplify';

// In order to mock routes in unit test, there can't be a global router. To test router use createLocalVue().
if (process.env.NODE_ENV !== 'test') {
  Vue.use(Router);
}

const originalReplace = Router.prototype.replace;
Router.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') throw err;
  });
};

async function requireAuth(to, from, next) {
  let signedIn = false;

  try {
    await Auth.currentAuthenticatedUser();
    signedIn = true;
  } catch {
    signedIn = false;
  }

  if (signedIn) {
    store.dispatch('getUserInfo').then(() => {
      store.dispatch('getProducts');
      next();
    });
  } else {
    store.dispatch('login', false);
    localStorage.setItem('redirect', to.fullPath);
    next({
      path: '/login',
      query: { redirect: to.fullPath },
    });
  }
}

function contentDefault(to, from, next) {
  localStorage.setItem('content-default', to.name);
  next();
}

function canCustomize(to, from, next) {
  store.commit('removeActivationQueue');
  if (!cognitoAuth.canCustomizeContent()) {
    next({ name: 'no-access' });
  }
  next();
}

export default new Router({
  base: process.env.BASE_URL,
  mode: 'history',
  linkExactActiveClass: 'active',
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      beforeEnter: requireAuth,
      redirect: { name: 'bizlibrary-content' },
      children: [
        {
          path: 'content/bizlibrary',
          name: 'bizlibrary-content',
          beforeEnter: (to, from, next) => {
            store.commit('removeActivationQueue');
            next();
          },
          redirect: () => {
            const name = localStorage.getItem('content-default') || 'content-list';
            return { name };
          },
          component: () => import('@/views/BizLibraryContent.vue'),
          children: [
            {
              path: 'grid',
              name: 'content-grid',
              beforeEnter: contentDefault,
              component: () => import('@/components/shared/CardGrid.vue'),
              meta: {
                layout: 'fixed',
              },
            },
            {
              path: 'list',
              name: 'content-list',
              beforeEnter: contentDefault,
              component: () => import('@/components/shared/data-table/DataTable.vue'),
              meta: {
                helpCategory: 'content.list',
                layout: 'fixed',
              },
            },
          ],
        },
        {
          path: 'content/custom',
          name: 'custom-content',
          beforeEnter: canCustomize,
          component: () => import('@/views/CustomContent.vue'),
          redirect: { name: 'custom-content-list' },
          children: [
            {
              path: 'list',
              name: 'custom-content-list',
              component: () => import('@/components/shared/data-table/DataTable.vue'),
              meta: {
                helpCategory: 'content.list',
                layout: 'fixed',
              },
            },
          ],
        },
        {
          path: 'content/bizlibrary/:syncguid',
          component: () => import('@/views/content-profile/ContentProfile.vue'),
          redirect: { name: 'bizlibrary-content-details' },
          children: ContentProfileRoutes.getRoutes('bizlibrary'),
        },
        {
          path: 'content/custom/:syncguid',
          beforeEnter: canCustomize,
          component: () => import('@/views/content-profile/ContentProfile.vue'),
          redirect: { name: 'custom-content-details' },
          children: ContentProfileRoutes.getRoutes('custom'),
        },
        {
          path: '/profile',
          component: () => import('@/views/MyProfile.vue'),
          children: [
            {
              path: '',
              name: 'profile',
              component: () => import('@/components/profile/Profile.vue'),
              meta: {
                helpCategory: 'profile',
              },
            },
          ],
        },
        {
          path: 'queue',
          name: 'content-queue',
          component: () => import('@/views/Queue.vue'),
          meta: {
            helpCategory: 'queue',
            layout: 'fixed',
          },
        },
        {
          path: 'log',
          name: 'content-log',
          component: () => import('@/views/ContentLog.vue'),
          meta: {
            layout: 'fixed',
            helpCategory: 'content-log',
          },
        },
        {
          path: '/help',
          name: 'help',
          component: () => import('@/views/Help.vue'),
          meta: {
            layout: 'fixed',
          },
        },
        {
          path: '/authentication',
          name: 'authentication',
          component: () => import('@/views/Authentication.vue'),
          meta: {
            layout: 'fixed',
          },
        },
        {
          path: '/ocn',
          name: 'ocn',
          component: () => import('@/views/OCNSetup.vue'),
          meta: {
            layout: 'fixed',
          },
        },
        {
          path: '/products',
          name: 'my-products',
          component: () => import('@/views/MyProducts.vue'),
          meta: {
            layout: 'fixed',
          },
        },
        {
          path: '/activations',
          name: 'my-activations',
          component: () => import('@/views/MyActivations.vue'),
          meta: {
            layout: 'fixed',
          },
          beforeEnter: (to, from, next) => {
            store.commit('removeActivationQueue');
            next();
          },
        },
        {
          path: '/no-access',
          name: 'no-access',
          component: () => import('@/views/NoAccess.vue'),
          meta: {
            layout: 'fixed',
          },
        },
      ],
    },
    {
      path: '/auth-success',
      name: 'auth-success',
      component: () => import('@/views/AuthSuccess.vue'),
    },
    {
      path: '/login',
      name: 'login',
      beforeEnter() {
        Auth.federatedSignIn();
      },
    },
    {
      path: '/login/oauth2/code/cognito/',
      beforeEnter(to, from, next) {
        const redirect = localStorage.getItem('redirect');
        if (redirect !== null) {
          localStorage.removeItem('redirect');
          next({ path: redirect });
        } else {
          next({ name: 'home' });
        }
      },
    },
    {
      path: '/logout',
      name: 'logout',
      beforeEnter(to, from, next) {
        Auth.signOut();
        next({ name: 'login' });
      },
    },
    {
      path: '/error',
      component: ErrorComponent,
    },
    {
      path: '/expired',
      name: 'expired',
      component: Expired,
    },
    {
      path: '*',
      component: () => import('@/views/404.vue'),
    },
  ],
});
